import type { EventSink } from '../events'
import { bodyOverlaps } from '../geom'
import { canBeHit, hit } from '../items/hit'
import type { Aabb, HazardDef, HazardPhase } from '../track/types'
import { nearBody } from '../track/xindex'
import type { World } from '../world'

/** Phase and hit box of a hazard at `tick` (table lookup, D-020). */
export function hazardAt(h: HazardDef, tick: number): { phase: HazardPhase; box: Aabb } {
  const k = (tick + h.phaseTicks) % h.periodTicks
  return { phase: h.phases[k]!, box: h.boxes[k]! }
}

/** Telegraph, waiting, retracted and resting never hit. */
export function isHarmful(phase: HazardPhase): boolean {
  return (
    phase === 'swinging' ||
    phase === 'extended' ||
    phase === 'falling' ||
    phase === 'armed' ||
    phase === 'rolling'
  )
}

/**
 * Step 4: a racing, non-respawning player overlapping a harmful hazard box is hit. Hazards are
 * evaluated at `tick + 1`, the state this step produces (like platforms), so what the renderer
 * draws for `world.tick` after the step is what was tested. One hit per player per tick, through
 * `hit()` (shields apply).
 */
export function stepHazards(world: World, events: EventSink): void {
  const { track } = world
  const at = world.tick + 1
  for (let i = 0; i < world.players.length; i++) {
    if (!canBeHit(world, i)) continue
    const p = world.players[i]!
    for (const k of nearBody(track.index.hazards, p)) {
      const h = track.hazards[k]!
      const { phase, box } = hazardAt(h, at)
      if (isHarmful(phase) && bodyOverlaps(p, box)) {
        hit(world, i, h.kind, h.stunTicks, events)
        break
      }
    }
  }
}
